import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { Code, FileSpreadsheet, Eye, Loader, Bot, Send, Plus, Mic, ChevronDown, ChevronUp, Maximize2, Minimize2, History, Save, X, Search, RotateCcw } from "lucide-react";
import * as XLSX from 'xlsx';
import { getApiBaseUrl, getPermissions } from "../api/configHelper";


// -----------------------------
// AdvanceReport.js (final)
// -----------------------------

// Helper: read current user and sessId from sessionStorage (fallbacks)
const getCurrentUser = () => sessionStorage.getItem("userName") || "admin";
const getSessId = () => sessionStorage.getItem("SessId") || sessionStorage.getItem("logId") || "admin";
const AI_ASSISTANT_AUTH = "Basic YWRtaW46YWRtaW4=";
const getDatabaseKey = () => sessionStorage.getItem("databaseKey") || "pgpl2026";

const AI_REPORT_ENDPOINTS = {
  analyze: "api/AiReport/analyze",
  savePrompt: "api/AiReport/SaveAIPrompt",
  promptHistory: "api/AiReport/GetAIPromptHistory",
};

const normalizeBaseUrl = (value) => String(value || "").trim().replace(/\/+$/, "");

const stripAiReportEndpoint = (value) =>
  normalizeBaseUrl(value).replace(/\/api\/AiReport\/(?:analyze|SaveAIPrompt|GetAIPromptHistory).*$/i, "");

const getValueIgnoreCase = (obj, key) => {
  if (!obj || typeof obj !== "object") return "";
  const foundKey = Object.keys(obj).find((itemKey) => itemKey.toLowerCase() === key.toLowerCase());
  return foundKey ? obj[foundKey] : "";
};

const getApiWebPathFromSession = () => {
  try {
    const rawMenus = sessionStorage.getItem("allSubMenuList");
    if (!rawMenus) return "";

    const parsedMenus = JSON.parse(rawMenus);
    const menuRows = Array.isArray(parsedMenus)
      ? parsedMenus
      : [
          ...(Array.isArray(parsedMenus?.Table) ? parsedMenus.Table : []),
          ...(Array.isArray(parsedMenus?.table) ? parsedMenus.table : []),
        ];

    const apiWebPath = menuRows
      .map((menu) => getValueIgnoreCase(menu, "apiwebpath"))
      .find(Boolean);

    return stripAiReportEndpoint(apiWebPath);
  } catch (error) {
    console.warn("Unable to read apiwebpath from allSubMenuList", error);
    return "";
  }
};

const getAiReportApiUrl = async (endpointKey) => {
  let baseUrl = "";

  try {
    baseUrl = normalizeBaseUrl(await getApiBaseUrl());
  } catch (error) {
    console.warn("Unable to load API_BASE_URL from config.json", error);
  }

  if (!baseUrl) {
    baseUrl = getApiWebPathFromSession();
  }

  if (!baseUrl) {
    throw new Error("API base URL is missing from config.json and allSubMenuList apiwebpath");
  }

  return `${baseUrl}/${AI_REPORT_ENDPOINTS[endpointKey]}`;
};

// -----------------------------
// DataTable Component
// -----------------------------
const DataTable = ({ data }) => {
  const [filters, setFilters] = React.useState({});
  const [search, setSearch] = React.useState("");

  if (!data || data.length === 0)
    return (
      <div className="flex items-center justify-center min-h-[220px] border rounded-md text-sm" style={{ color: 'var(--base-content)', borderColor: 'var(--base-300)', backgroundColor: 'var(--base-100)' }}>
        No data available
      </div>
    );

  const headers = Object.keys(data[0]);

  const filteredData = data.filter((row) => {
    if (search) {
      const rowStr = Object.values(row).join(" ").toLowerCase();
      if (!rowStr.includes(search.toLowerCase())) return false;
    }
    for (const h of headers) {
      if (
        filters[h] &&
        !String(row[h]).toLowerCase().includes(filters[h].toLowerCase())
      )
        return false;
    }
    return true;
  });

  const hasFilters = search || Object.values(filters).some(Boolean);
  const getColumnWidth = (header) => {
    const sampleRows = data.slice(0, 100);
    const maxContentLength = Math.max(
      String(header).length,
      ...sampleRows.map((row) => String(row[header] ?? "").length)
    );

    if (maxContentLength <= 3) return 72;
    if (maxContentLength <= 6) return 96;
    if (maxContentLength <= 10) return 130;
    if (maxContentLength <= 16) return 160;
    if (maxContentLength <= 24) return 210;
    return 280;
  };

  const columnWidths = headers.reduce((widths, header) => {
    widths[header] = getColumnWidth(header);
    return widths;
  }, {});

  const tableMinWidth = headers.reduce((total, header) => total + columnWidths[header], 0);

  return (
    <div className="flex flex-col w-full h-full min-h-0 overflow-hidden border rounded-md shadow-sm" style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1' }}>
      <style>
        {`
          .advance-report-grid {
            scrollbar-gutter: stable both-edges;
          }
          .advance-report-grid::-webkit-scrollbar {
            width: 14px;
            height: 14px;
          }
          .advance-report-grid::-webkit-scrollbar-track {
            background: #eef2f7;
          }
          .advance-report-grid::-webkit-scrollbar-thumb {
            background: #b9c6d8;
            border: 2px solid #eef2f7;
            border-radius: 999px;
          }
          .advance-report-grid::-webkit-scrollbar-thumb:hover {
            background: var(--primary);
          }
          .advance-report-grid table {
            border-collapse: separate;
            border-spacing: 0;
          }
          .advance-report-grid th:first-child,
          .advance-report-grid td:first-child {
            position: sticky;
            left: 0;
            z-index: 15;
            box-shadow: 1px 0 0 #cbd5e1;
          }
          .advance-report-grid thead th:first-child {
            z-index: 35;
          }
          .advance-report-grid tbody tr:hover td {
            background-color: #eaf2ff !important;
          }
          .advance-report-grid tbody tr:hover td:first-child {
            background-color: #eaf2ff !important;
          }
        `}
      </style>

      <div className="flex items-center justify-end gap-2 px-2 py-1 border-b" style={{ backgroundColor: '#f8fafc', borderColor: '#d7dee8' }}>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute w-4 h-4 -translate-y-1/2 left-2 top-1/2" style={{ color: 'var(--base-content)', opacity: 0.55 }} />
            <input
              className="w-60 max-w-[48vw] rounded border py-1 pl-8 pr-2 text-xs outline-none focus:ring-2"
              style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#1f2937' }}
              placeholder="Search all columns"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="flex items-center justify-center w-7 h-7 border rounded disabled:opacity-40"
            style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#334155' }}
            title="Clear filters"
            disabled={!hasFilters}
            onClick={() => {
              setSearch("");
              setFilters({});
            }}
          >
            <RotateCcw size={15} />
          </button>
        </div>
      </div>

      <div className="advance-report-grid relative flex-1 min-h-0 overflow-scroll" style={{ scrollbarWidth: 'auto', scrollbarColor: '#b9c6d8 #eef2f7' }}>
        <table className="w-full text-left table-fixed" style={{ minWidth: `${Math.max(tableMinWidth, 900)}px` }}>
          <colgroup>
            {headers.map((h) => (
              <col key={h} style={{ width: `${columnWidths[h]}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr className="sticky top-0 z-30" style={{ backgroundColor: "#eef2f7" }}>
              {headers.map((h) => (
                <th
                  key={h}
                  className="px-2 py-1.5 text-[11px] font-semibold uppercase border-b border-r whitespace-nowrap"
                  title={h}
                  style={{ borderColor: "#cbd5e1", color: "#0f2f6b", backgroundColor: "#eef2f7" }}
                >
                  <div className="max-w-[220px] truncate">{h}</div>
                </th>
              ))}
            </tr>

            <tr className="sticky top-[29px] z-30" style={{ backgroundColor: "#ffffff" }}>
              {headers.map((h) => (
                <th key={h} className="p-0.5 border-b border-r" style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff" }}>
                  <input
                    className="w-full px-1.5 py-0.5 text-xs border rounded outline-none focus:ring-1"
                    style={{ backgroundColor: '#f8fafc', borderColor: '#d7dee8', color: '#1f2937' }}
                    placeholder="Filter"
                    value={filters[h] || ""}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, [h]: e.target.value }))
                    }
                  />
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="group"
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc',
                }}
              >
                {headers.map((h, cellIndex) => (
                  <td
                    key={h}
                    className="px-2 py-1 text-[13px] border-b border-r whitespace-nowrap font-mono"
                    title={row[h] == null ? "" : String(row[h])}
                    style={{
                      borderColor: '#d7dee8',
                      color: '#111827',
                      fontWeight: 500,
                      backgroundColor: cellIndex === 0 ? (rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc') : undefined,
                    }}
                  >
                    <div className="max-w-[260px] overflow-hidden text-ellipsis">
                      {row[h] == null || row[h] === "" ? <span className="opacity-40">-</span> : String(row[h])}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {filteredData.length === 0 && (
              <tr>
                <td colSpan={headers.length} className="px-4 py-10 text-sm text-center" style={{ color: 'var(--base-content)' }}>
                  No rows match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -----------------------------
// TabsComponent
// -----------------------------
const TabsComponent = ({ tables }) => {
  const table2 = tables?.Table2 || [];
  const tabs = table2.map(item => ({
    name: item.TblName,
    tableKey: `Table${2 + parseInt(item.TblNo)}`,
    data: tables[`Table${2 + parseInt(item.TblNo)}`] || []
  })).filter(tab => tab.tableKey !== "Table3" && tab.data.length > 0);
  const [activeTab, setActiveTab] = React.useState(tabs[0]?.name || '');
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (tabs.length === 0) {
    return <DataTable data={[]} />;
  }

  const activeData = tabs.find(t => t.name === activeTab)?.data || [];

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: 'var(--base-100)' }}>
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b" style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1' }}>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold truncate" style={{ color: '#1f2937' }}>{activeTab}</h3>
          </div>
          <button onClick={() => setIsExpanded(false)} className="flex items-center justify-center w-8 h-8 border rounded" title="Minimize" style={{ backgroundColor: '#ffffff', color: '#1e3a8a', borderColor: '#cbd5e1' }}>
            <Minimize2 size={18} />
          </button>
        </div>
        <div className="flex-1 min-h-0 p-1 overflow-hidden">
          <DataTable data={activeData} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden border rounded-md shadow-sm" style={{ borderColor: '#cbd5e1', backgroundColor: '#ffffff' }}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b" style={{ borderColor: '#cbd5e1', backgroundColor: '#f8fafc' }}>
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.name}
              type="button"
              className="px-3 py-1.5 text-xs font-semibold border rounded whitespace-nowrap"
              style={{
                backgroundColor: activeTab === tab.name ? 'var(--primary)' : '#ffffff',
                borderColor: activeTab === tab.name ? 'var(--primary)' : '#cbd5e1',
                color: activeTab === tab.name ? 'var(--primary-content)' : '#1e3a8a',
              }}
              onClick={() => setActiveTab(tab.name)}
              title={`${tab.data.length} rows`}
            >
              {tab.name}
              <span className="ml-2 opacity-70">{tab.data.length}</span>
            </button>
          ))}
        </div>
        <button onClick={() => setIsExpanded(true)} className="flex items-center justify-center w-8 h-8 border rounded shrink-0" title="Expand Table" style={{ backgroundColor: '#ffffff', color: '#1e3a8a', borderColor: '#cbd5e1' }}>
          <Maximize2 size={18} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab && <DataTable data={activeData} />}
      </div>
    </div>
  );
};

// -----------------------------
// SearchableSelect (single-select with inline search)
// -----------------------------
const SearchableSelect = ({ options, value, onChange, placeholder, isLoading, autoFocus = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");
  const [selectedIndex, setSelectedIndex] = React.useState(-1);

  React.useEffect(() => {
    if (value) {
      const sel = options.find((o) => o.value == value);
      setInputValue(sel ? sel.label : "");
    } else {
      setInputValue("");
    }
  }, [value, options]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setSearchTerm(e.target.value);
    setSelectedIndex(-1);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (opt) => {
    onChange(opt.value);
    setInputValue(opt.label);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleFocus = () => {
    setSearchTerm(inputValue);
    setSelectedIndex(-1);
  };

  const handleBlur = () => {
    // small delay to allow click on option
    setTimeout(() => setIsOpen(false), 150);
  };

  const filtered = options.filter((o) =>
    String(o.label).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative">
      <input
        type="text"
        className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={() => setIsOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isOpen) {
            e.preventDefault();
            setIsOpen(true);
            return;
          }
          if (!isOpen) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, -1));
          } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            handleSelect(filtered[selectedIndex]);
          }
        }}
        disabled={isLoading}
        autoFocus={autoFocus}
      />

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 overflow-auto border rounded-md shadow-lg max-h-60" style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#111827' }}>
          {filtered.length > 0 ? (
            filtered.map((opt, idx) => (
              <div
                key={idx}
                onMouseDown={() => handleSelect(opt)} // use onMouseDown to avoid blur race
                className={`px-4 py-2 text-sm cursor-pointer ${selectedIndex === idx ? 'bg-blue-100' : 'hover:bg-slate-100'}`}
              >
                {opt.label}
              </div>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500">No matching options</div>
          )}
        </div>
      )}
    </div>
  );
};

// -----------------------------
// ModalMultiSelect (opaque white modal; uses Table3 for options)
// -----------------------------
const ModalMultiSelect = ({ options, value, onChange, title, isLoading }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const toggleOption = (optValue) => {
    const currentValues = Array.isArray(value) ? value : [];
    let newValues;
    if (optValue === "All") {
      newValues = currentValues.includes("All") ? [] : ["All"];
    } else {
      let temp = currentValues.filter((v) => v !== "All");
      newValues = temp.includes(optValue) ? temp.filter((v) => v !== optValue) : [...temp, optValue];
    }
    onChange(newValues);
  };

  const getDisplayText = () => {
    if (isLoading) return "Loading options...";
    if (!value || value.length === 0) return "Select options...";
    if (value.includes("All")) return "All Records Selected";
    const labels = options.filter((o) => value.includes(o.value)).map((o) => o.label);
    if (labels.length > 2) return `${labels.length} items selected`;
    return labels.join(", ");
  };

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative">
      <div
        onClick={() => !isLoading && setIsOpen(true)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <span className="text-sm truncate">{getDisplayText()}</span>
        <ChevronDown size={16} className="text-gray-500" />
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[80vh] flex flex-col border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
              <button className="text-xl text-gray-500 hover:text-gray-700" onClick={() => setIsOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <input
              className="w-full px-3 py-2 mb-4 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />

            <div className="flex-1 pr-1 space-y-1 overflow-y-auto">
              {filtered.length > 0 ? (
                filtered.map((opt, idx) => (
                  <label
                    key={idx}
                    className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover:bg-gray-50"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      toggleOption(opt.value);
                    }}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      checked={Array.isArray(value) && value.includes(opt.value)}
                      readOnly
                    />
                    <span className="flex-1 text-sm">{opt.label}</span>
                    <span className="text-xs text-gray-500">{opt.value}</span>
                  </label>
                ))
              ) : (
                <div className="py-8 text-sm text-center text-gray-500">No options found</div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => setIsOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -----------------------------
// Main ReportGenerator (uses reportsApi)
// -----------------------------
const ReportGenerator = () => {
  const navigate = useNavigate();
  const [reportList, setReportList] = React.useState([]);
  const [reportConfig, setReportConfig] = React.useState(null); // { id, parameters: [...] }
  const [dropdownOptions, setDropdownOptions] = React.useState({}); // keyed by paramName
  const [selectedReportId, setSelectedReportId] = React.useState("");
  const [formValues, setFormValues] = React.useState({});
  const [outputModal, setOutputModal] = React.useState({ show: false, excel: "", pdf: "", view: "actions" });

  const [loadingReports, setLoadingReports] = React.useState(true);
  const [loadingParams, setLoadingParams] = React.useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [reportData, setReportData] = React.useState(null);
  const [userParamList, setUserParamList] = React.useState('');
  const [showAiChat, setShowAiChat] = React.useState(false);
  const [aiIsMinimized, setAiIsMinimized] = React.useState(false);
  const [aiMessages, setAiMessages] = React.useState([]);
  const [aiInputText, setAiInputText] = React.useState("");
  const [aiAttachments, setAiAttachments] = React.useState([]);
  const [aiIsLoading, setAiIsLoading] = React.useState(false);
  const [aiIsListening, setAiIsListening] = React.useState(false);
  const [aiChatPosition, setAiChatPosition] = React.useState(null);
  const [aiIsDragging, setAiIsDragging] = React.useState(false);
  const [aiPromptSaveStatus, setAiPromptSaveStatus] = React.useState("");
  const [showAiPromptHistory, setShowAiPromptHistory] = React.useState(false);
  const [aiPromptHistory, setAiPromptHistory] = React.useState([]);
  const [aiPromptHistoryLoading, setAiPromptHistoryLoading] = React.useState(false);
  const [aiPromptHistoryError, setAiPromptHistoryError] = React.useState("");
  const aiFileInputRef = React.useRef(null);
  const aiMessagesEndRef = React.useRef(null);
  const aiChatCardRef = React.useRef(null);
  const aiDragStateRef = React.useRef(null);
  const aiSavedPromptKeysRef = React.useRef(new Set());

  const activeReportTitle = String(reportData?.Table?.[0]?.ReportTitle || reportConfig?.meta?.Reportname || "").trim();
  const getActiveReportTitle = () => activeReportTitle;
  const isDataView = outputModal.view === "data";

  React.useEffect(() => {
    setAiPromptHistory([]);
    setAiPromptHistoryError("");
    setShowAiPromptHistory(false);
  }, [activeReportTitle]);

  const resetAiAssistantForReport = () => {
    setAiMessages([]);
    setAiInputText("");
    setAiAttachments([]);
    setAiIsLoading(false);
    setAiIsListening(false);
    setAiPromptSaveStatus("");
    setAiPromptHistory([]);
    setAiPromptHistoryError("");
    setShowAiPromptHistory(false);
    aiSavedPromptKeysRef.current = new Set();
  };

  const closeReportOutput = () => {
    setOutputModal({ show: false, excel: "", pdf: "", view: "actions" });
    setReportData(null);
    setShowAiChat(false);
    setAiIsMinimized(false);
  };

  const openReportPreview = () => {
    let htmlContent = reportData?.Table?.[0]?.HTMlString || "";

    // Collect dynamic tables
    const dynamicJSON = {};
    Object.keys(reportData || {}).forEach(key => {
      if (key.startsWith("Table")) {
        dynamicJSON[key] = reportData[key];
      }
    });

    // Base64 encode JSON (prevents ALL syntax errors)
    const encodedJSON = btoa(unescape(encodeURIComponent(JSON.stringify(dynamicJSON))));

    // JS to decode it back
    const jsonScript = `
        const tableData = JSON.parse(decodeURIComponent(escape(atob("${encodedJSON}"))));
    `;

    // Inject into HTML
    htmlContent = htmlContent.replace("<!--RMPJSONDATA--!>", jsonScript);

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    URL.revokeObjectURL(url);
  };

  const showReportData = () => {
    setOutputModal((prev) => ({ ...prev, view: "data" }));
  };

  const showReportDataWithAi = () => {
    showReportData();
    setAiIsMinimized(false);
    setAiChatPosition(null);
    setShowAiChat(true);
  };

  // Load report list from API
  React.useEffect(() => {
    const load = async () => {
      try {
        setLoadingReports(true);
        const user = getCurrentUser();
        const resp = await api.AdvRptGetReportList(user);
        // API returns { Table: [...] }
        const reports = resp?.Table || [];
        // map to id/name
        const mapped = reports.map((r) => ({ id: r.Rptid ?? r.RptId ?? r.rptid, name: r.Reportname ?? r.Reportname ?? r.ReportName }));
        setReportList(mapped);
      } catch (err) {
        console.error("Failed to load report list", err);
      } finally {
        setLoadingReports(false);
      }
    };
    load();
  }, []);

  // Scroll to bottom for AI messages
  const scrollAiToBottom = () => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    scrollAiToBottom();
  }, [aiMessages]);

  // AI Assistant history: save prompt only when the user clicks Save Chat.
  const saveAiPromptHistory = async (prompt) => {
    try {
      const databaseKey = encodeURIComponent(getDatabaseKey());
      const reportTitle = getActiveReportTitle();
      const savePromptUrl = await getAiReportApiUrl("savePrompt");
      const response = await fetch(`${savePromptUrl}?databaseKey=${databaseKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": AI_ASSISTANT_AUTH,
        },
        body: JSON.stringify({
          id: 0,
          username: getCurrentUser(),
          prompt,
          reportTitle,
        }),
      });

      if (!response.ok) {
        throw new Error(`SaveAIPrompt failed with status ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("AI prompt history save error:", error);
      return null;
    }
  };

  const handleSaveAiMessagePrompt = async (prompt, key) => {
    const trimmedPrompt = String(prompt || "").trim();
    if (!trimmedPrompt) return;

    if (aiSavedPromptKeysRef.current.has(key)) {
      setAiPromptSaveStatus("Prompt already saved");
      return;
    }

    setAiPromptSaveStatus("Saving...");
    const result = await saveAiPromptHistory(trimmedPrompt);
    if (result?.status === 1 || result?.message) {
      aiSavedPromptKeysRef.current.add(key);
      setAiPromptSaveStatus("Prompt saved");
      return;
    }

    setAiPromptSaveStatus("Unable to save prompt");
  };

  const loadAiPromptHistory = async () => {
    setAiPromptHistoryLoading(true);
    setAiPromptHistoryError("");

    try {
      const databaseKey = encodeURIComponent(getDatabaseKey());
      const reportTitle = getActiveReportTitle();
      const promptHistoryUrl = await getAiReportApiUrl("promptHistory");
      const response = await fetch(`${promptHistoryUrl}?databaseKey=${databaseKey}&ReportTitle=${encodeURIComponent(reportTitle)}`, {
        method: "GET",
        headers: {
          "Authorization": AI_ASSISTANT_AUTH,
        },
      });

      if (!response.ok) {
        throw new Error(`GetAIPromptHistory failed with status ${response.status}`);
      }

      const data = await response.json();
      const titleKey = reportTitle.toLowerCase();
      const filteredHistory = (data?.Table || []).filter((item) =>
        String(item.ReportTitle || "").trim().toLowerCase() === titleKey
      );
      setAiPromptHistory(filteredHistory);
    } catch (error) {
      console.error("AI prompt history load error:", error);
      setAiPromptHistoryError("Unable to load prompt history");
    } finally {
      setAiPromptHistoryLoading(false);
    }
  };

  const handleToggleAiPromptHistory = () => {
    const nextShowHistory = !showAiPromptHistory;
    setShowAiPromptHistory(nextShowHistory);
    if (nextShowHistory && aiPromptHistory.length === 0) {
      loadAiPromptHistory();
    }
  };

  const getBoundedAiChatPosition = (left, top, width, height) => {
    const margin = 12;
    const maxLeft = Math.max(margin, window.innerWidth - width - margin);
    const maxTop = Math.max(margin, window.innerHeight - height - margin);

    return {
      left: Math.min(Math.max(left, margin), maxLeft),
      top: Math.min(Math.max(top, margin), maxTop),
    };
  };

  // AI Assistant floating card: drag by header with direct pointer positioning.
  const handleAiChatDragStart = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (!aiChatCardRef.current) return;
    event.preventDefault();

    const rect = aiChatCardRef.current.getBoundingClientRect();
    aiDragStateRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    setAiIsDragging(true);
    setAiChatPosition(getBoundedAiChatPosition(rect.left, rect.top, rect.width, rect.height));

    const handleDragMove = (moveEvent) => {
      if (!aiDragStateRef.current) return;
      const { offsetX, offsetY, width, height } = aiDragStateRef.current;

      setAiChatPosition(
        getBoundedAiChatPosition(
          moveEvent.clientX - offsetX,
          moveEvent.clientY - offsetY,
          width,
          height
        )
      );
    };

    const handleDragEnd = () => {
      aiDragStateRef.current = null;
      setAiIsDragging(false);
      window.removeEventListener("pointermove", handleDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
    };

    window.addEventListener("pointermove", handleDragMove);
    window.addEventListener("pointerup", handleDragEnd);
  };

  // When user selects a report, call AdvRpt-GetUserControls
  const handleReportSelect = async (rptId) => {
    if (String(rptId) !== String(selectedReportId)) {
      resetAiAssistantForReport();
    }
    setSelectedReportId(rptId);
    setReportConfig(null);
    setFormValues({});
    setDropdownOptions({});

    if (!rptId) return;

    try {
      setLoadingParams(true);
      const user = getCurrentUser();
      const sessId = getSessId();
      const resp = await api.AdvRptGetUserControls(user, rptId, sessId);
      const rptMeta = resp?.Table?.[0] || resp?.GetRefreshData?.Table?.[0] || null;
      const params = (resp?.Table1 || []).map((p) => ({
        slNo: p.SlNo,
        title: p.ParamTitle || p.ParamName || p.Param,
        name: p.ParamName,
        controlType: p.ControlType,
        defaultValue: p.DefaultValue,
        tblName: p.Tblname,
        primeValue: p.Primevalue,
        userValue: p.UserValue,
        addlColumns: p.addlColumns,
        isMultiSelect: p.IsMultiSelect,
      }));

      // initialize form values
      const initial = {};
      params.forEach((p) => {
        if (p.controlType?.toLowerCase() === "datetime") {
          // set all date fields to current date
          initial[p.name] = new Date().toISOString().split("T")[0];
        } else if (p.controlType?.toLowerCase() === "dropdown") {
          if (p.isMultiSelect === 1) {
            initial[p.name] = p.defaultValue === "All" ? ["All"] : [];
          } else {
            initial[p.name] = p.defaultValue || "";
          }
        } else {
          initial[p.name] = p.defaultValue ?? "";
        }
      });

      setReportConfig({ id: rptId, parameters: params, meta: rptMeta   });
      setFormValues(initial);

      // Build dropdown options from Table3 (per your choice A)
      const table3 = resp?.Table3 || [];
      // For each dropdown param, create options using primeValue or userValue or fallback to the first key
    // Build dropdown options using matching table
const tables = [resp.Table3, resp.Table4, resp.Table5, resp.Table6].filter(Boolean);

params.forEach((p) => {
  if (p.controlType?.toLowerCase() !== "dropdown") return;

  setLoadingDropdowns((s) => ({ ...s, [p.name]: true }));

  const prime = p.primeValue;
  const user = p.userValue;

  let matchedTable = null;

  // FIND TABLE THAT CONTAINS BOTH COLUMNS
  for (const tbl of tables) {
    if (!tbl || tbl.length === 0) continue;

    const firstRow = tbl[0];
    const hasPrime = prime && Object.prototype.hasOwnProperty.call(firstRow, prime);
    const hasUser = user && Object.prototype.hasOwnProperty.call(firstRow, user);

    if (hasPrime || hasUser) {
      matchedTable = tbl;
      break;
    }
  }

  // If still not found, fallback to first table
  if (!matchedTable) matchedTable = tables[0] || [];

  // Create dropdown options
  const opts = matchedTable.map((row) => ({
    value: row[prime] ?? row[user] ?? Object.values(row)[0],
    label: row[user] ?? row[prime] ?? Object.values(row)[0],
  }));

  // Insert ALL
  const dedup = [];
  const seen = new Set();
 // dedup.push({ value: "All", label: "All" });

  for (const o of opts) {
    const val = String(o.value);
    if (!seen.has(val)) {
      seen.add(val);
      dedup.push({
        value: val,
        label: String(o.label),
      });
    }
  }

  setDropdownOptions((prev) => ({ ...prev, [p.name]: dedup }));
  setLoadingDropdowns((s) => ({ ...s, [p.name]: false }));
});

    } catch (err) {
      console.error("Failed to load report parameters", err);
    } finally {
      setLoadingParams(false);
    }
  };

    async function getUserIP() {
  try {
    const res = await fetch("https://api.ipify.org/?format=json");
    const data = await res.json();
    return data.ip;  
  } catch {
    return "";
  }
}
  const updateFormValue = (name, val) => {
    setFormValues((prev) => ({ ...prev, [name]: val }));
  };

const prepareReport = async (e) => {
  e.preventDefault();
  setSubmitting(true);
  let validationErrors = [];
  reportConfig.parameters.forEach((param) => {
    if (param.controlType?.toLowerCase() === "dropdown") {
      const val = formValues[param.name];
      if (param.isMultiSelect === 1) {
        if (!val || val.length === 0) {
          validationErrors.push(`Please select at least one value for ${param.title}`);
        }
      } else {
        if (!val || val === "") {
          validationErrors.push(`Please select a value for ${param.title}`);
        }
      }
    }
  });
  if (validationErrors.length > 0) {
    alert(validationErrors.join('\n'));
    setSubmitting(false);
    return;
  }

  try {
    const userIP = await getUserIP();
    const user = getCurrentUser();
    const sessId = getSessId();
    const databaseKey = sessionStorage.getItem("databaseKey") || "";

    const paramParts = [];
    const userParamListArray = [];
    (reportConfig?.parameters || []).forEach((param) => {
      const rawValue = formValues[param.name];
      let apiValue = "";

      if (param.controlType?.toLowerCase() === "dropdown") {
        if (Array.isArray(rawValue)) {
          apiValue = rawValue.includes("All") ? "All" : rawValue.join(",");
        } else {
          apiValue = rawValue ?? "";
        }
      } else {
        apiValue = rawValue ?? "";
      }

      paramParts.push(` ${param.name} ='${apiValue}'`);

      // For userParamList, use display value (label) instead of apiValue
      let displayValue = apiValue;
      if (param.controlType?.toLowerCase() === "dropdown") {
        const opts = dropdownOptions[param.name] || [];
        if (Array.isArray(rawValue)) {
          if (rawValue.includes("All")) {
            displayValue = "All";
          } else {
            displayValue = rawValue.map(v => {
              const opt = opts.find(o => o.value == v);
              return opt ? opt.label : v;
            }).join(", ");
          }
        } else {
          const opt = opts.find(o => o.value == rawValue);
          displayValue = opt ? opt.label : rawValue;
        }
      }

      if (displayValue) {
        userParamListArray.push({paramTitle: param.title, value: displayValue});
      }
    });

    const userParamListString = " select Input List \\ " + userParamListArray.map(item => `${item.paramTitle}=${item.value}`).join(' \\ ');
    setUserParamList(userParamListString);

    paramParts.push(` @Usrname ='${user}'`);
    paramParts.push(` @SessId ='${sessId}'`);

    const paramString = paramParts.join(" ,");

   const payload = {
  item: {
    rptid: Number(selectedReportId),
    mdlid: String(selectedReportId),
    procName: reportConfig?.meta?.PRoceName || "",
    paramandvalues: paramString,
    userParamList: userParamListString,
    userName: user,
    isExcelNeed: 1
  },
  log: {
    frontEndServerIP: window.location.hostname,
    userServerIP:  userIP,
    moduleName: "Advance Rmp Report" ,
    functionName: "AdvRptGenerate",
    primeId:"0",
    mdlId: String(selectedReportId),
    primeIdColValue:"CallId",
    moduleColName: "ReportName",
    browserInfo: navigator.userAgent,
    moduleValue: reportConfig?.meta?.Reportname || ""
  }
};

    const result = await api.AdvRptCreateCallId(payload);
    console.log("API RESPONSE:", result);

	    resetAiAssistantForReport();
	    setReportData(result.finalData);
	    setOutputModal({ show: true, excel: "", pdf: "", view: "actions" });

  } catch (err) {
    console.error("Error generating report:", err);
    alert("Failed to generate report!");
  }

  setSubmitting(false);
};

  // AI Chat handlers
  const handleAiSend = async () => {
    const userQuestion = aiInputText.trim();
    if (!userQuestion) return;

 const userMessage = {
  role: 'user',
  content: userQuestion,
  attachments: aiAttachments.map(f => f.name),
  timestamp: new Date().toLocaleString()
};
    setAiMessages(prev => [...prev, userMessage]);
    setAiInputText("");
    setAiAttachments([]);
    setAiIsLoading(true);

    try {
      // AI Assistant correction: call only the new AI report API with the required payload.
      const bodyData = {
        userQuery: userQuestion,
        jsonData: JSON.stringify(reportData || {}),
        databaseKey: getDatabaseKey()
      };

      const analyzeUrl = await getAiReportApiUrl("analyze");
      const response = await fetch(analyzeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': AI_ASSISTANT_AUTH,
        },
        body: JSON.stringify(bodyData)
      });

      if (!response.ok) {
        throw new Error(`AI API failed with status ${response.status}`);
      }

      const data = await response.json();

      if (data.report_html || data.chart_html) {
        // AI Assistant correction: store the full new API response for a user-friendly display page.
     sessionStorage.setItem("aiReportResponse", JSON.stringify(data));
     sessionStorage.setItem("aiReportHtml", `${data.chart_html || ""}${data.report_html || ""}`);

const aiMessage = {
  role: "assistant",
  content: `
AI report ready!  
👉 <a href="#" class="text-purple-600 underline" onclick="window.open('/ai-report-display','aiReportPopup','width=1200,height=800,scrollbars=yes,resizable=yes');">View AI Result</a>
  `,
  timestamp: new Date().toLocaleString()
};
        setAiMessages(prev => [...prev, aiMessage]);
      } else {
        const aiMessage = {
          role: 'assistant',
          content: data.response || data.summary || "No response"
        };
        setAiMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error('AI API error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "Sorry, I couldn't process your request. Please try again."
      };
      setAiMessages(prev => [...prev, errorMessage]);
    } finally {
      setAiIsLoading(false);
    }
  };
    const [permissions, setPermissions] = useState({});

  useEffect(() => {
  const loadPermissions = async () => {
    try {
      const perms = await getPermissions();
      setPermissions(perms);
    } catch (err) {
      console.error("Failed to load permissions", err);
    }
  };
  loadPermissions();
}, []);

  const handleAiKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiSend();
    }
  };

  const handleAiFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAiAttachments(prev => [...prev, ...files]);
  };

  const startAiVoiceRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice recognition not supported in this browser');
      return;
    }

    const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setAiIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setAiInputText(prev => prev + transcript);
    };

    recognition.onend = () => {
      setAiIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setAiIsListening(false);
    };

    recognition.start();
  };

  return (
    <div className="h-full max-w-[1600px] mx-auto overflow-visible border shadow-lg rounded-lg" style={{ backgroundColor: 'var(--base-100)', borderColor: 'var(--base-300)' }}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b" style={{ backgroundColor: 'var(--base-100)', borderColor: 'var(--base-300)' }}>
        <div>
          <h2 className="text-xl font-bold md:text-2xl" style={{ color: 'var(--base-content)' }}>Advance Reports</h2>
          <p className="mt-1 text-xs opacity-70" style={{ color: 'var(--base-content)' }}>
            Select a report, enter parameters, and review output in a spreadsheet view.
          </p>
        </div>
        <div className="px-3 py-1 text-xs font-semibold border rounded-full" style={{ color: 'var(--base-content)', borderColor: 'var(--base-300)', backgroundColor: 'var(--base-200)' }}>
          {reportList.length} reports
        </div>
      </div>

      {/* Report selection */}
      {loadingReports ? (
        <div className="py-8 text-center text-gray-500">
          <Loader className="mx-auto mb-2 animate-spin" size={24} />
          Loading reports...
        </div>
      ) : (
        <div className="px-5 pt-5">
          <div className="grid items-end grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,520px)_1fr]">
            <div>
              <label className="block mb-2 text-sm font-semibold" style={{ color: 'var(--base-content)' }}>Select Report</label>
              <SearchableSelect
                options={reportList.map((r) => ({ value: r.id, label: `${r.name}` }))}
                value={selectedReportId}
                onChange={(val) => handleReportSelect(val)}
                placeholder="Select a report..."
                isLoading={loadingReports}
                autoFocus={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Parameters */}
      {loadingParams && (
        <div className="py-8 text-center text-gray-500">
          <Loader className="mx-auto mb-2 animate-spin" size={24} />
          Loading parameters...
        </div>
      )}

      {reportConfig && !loadingParams && (
        <form onSubmit={prepareReport} className="px-5 py-5 space-y-5">
          <div className="flex items-center justify-between pb-2 border-b" style={{ borderColor: 'var(--base-300)' }}>
            <div>
              <h3 className="text-base font-semibold" style={{ color: 'var(--base-content)' }}>Parameters</h3>
              <p className="mt-0.5 text-xs opacity-70" style={{ color: 'var(--base-content)' }}>{reportConfig.parameters.length} inputs required for this report</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {reportConfig.parameters.map((p) => (
              <div key={p.slNo} className="space-y-1.5">
                <label className="block text-xs font-semibold uppercase opacity-75" style={{ color: 'var(--base-content)' }}>{p.title}</label>

                {p.controlType?.toLowerCase() === "dropdown" ? (
                  p.isMultiSelect === 1 ? (
                    <ModalMultiSelect
                      options={dropdownOptions[p.name] || []}
                      value={formValues[p.name]}
                      onChange={(v) => updateFormValue(p.name, v)}
                      title={`Select ${p.title}`}
                      isLoading={loadingDropdowns[p.name]}
                    />
                  ) : (
                    <SearchableSelect
                      options={dropdownOptions[p.name] || []}
                      value={formValues[p.name]}
                      onChange={(v) => updateFormValue(p.name, v)}
                      placeholder={`Select ${p.title}...`}
                      isLoading={loadingDropdowns[p.name]}
                    />
                  )
                ) : p.controlType?.toLowerCase() === "datetime" ? (
                  <input
                    type="date"
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formValues[p.name] || ""}
                    onChange={(e) => updateFormValue(p.name, e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formValues[p.name] || ""}
                    onChange={(e) => updateFormValue(p.name, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="px-6 py-2 text-sm font-medium transition-colors shadow-sm btn-professional-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader className="inline mr-2 animate-spin" size={16} />
                  Generating...
                </>
              ) : (
                "Generate Report"
              )}
            </button>
          </div>
        </form>
      )}
{outputModal.show && (
  <div className="fixed inset-0 z-50 flex overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
  <div className="flex flex-col w-full h-screen min-h-0 shadow-2xl"
     style={{ backgroundColor: 'var(--base-100)' }}>

      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b shadow-sm" style={{ borderColor: '#cbd5e1', backgroundColor: '#ffffff' }}>
        <div className="flex-1 min-w-[260px]">
          <h3 className="text-base font-semibold truncate" style={{ color: 'var(--base-content)' }}>{reportData?.Table?.[0]?.ReportTitle || 'Report Output'}</h3>
          <p className="mt-0.5 text-xs opacity-70" style={{ color: 'var(--base-content)' }}>Spreadsheet view with filters and expandable tables</p>
        </div>
	        <button
	          className="flex items-center justify-center w-9 h-9 text-white transition-colors bg-red-600 rounded-md hover:bg-red-700"
	          title="Close"
	          onClick={closeReportOutput}
	        >
	          <X size={18} />
	        </button>
	      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Main Report Content */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0 gap-3 p-3 overflow-hidden transition-all duration-300">
           <div className="border rounded-md shadow-sm" style={{ borderColor: '#cbd5e1', backgroundColor: '#ffffff' }}>
            <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: '#cbd5e1', backgroundColor: '#f8fafc' }}>
              <label className="text-xs font-semibold uppercase" style={{ color: '#0f2f6b' }}>User Parameters</label>
            </div>
            <pre className="px-3 py-2 overflow-x-auto text-xs leading-5 max-h-20" style={{ color: '#1f2937', backgroundColor: '#ffffff' }}>{userParamList}</pre>
          </div>

	          {isDataView ? (
	            <div className="flex-1 min-h-0 overflow-hidden">
	              <TabsComponent tables={reportData} />
	            </div>
	          ) : (
	            <div className="flex flex-1 min-h-[320px] items-center justify-center overflow-auto rounded-md border p-4 sm:p-6" style={{ borderColor: '#d7dee8', backgroundColor: '#f8fafc' }}>
	              <div className="w-full max-w-5xl rounded-md border bg-white p-4 shadow-sm sm:p-6" style={{ borderColor: '#d7dee8' }}>
	                <div className="mb-5 flex flex-col gap-2 border-b pb-4 sm:flex-row sm:items-end sm:justify-between" style={{ borderColor: '#e2e8f0' }}>
	                  <div>
	                    <h4 className="text-base font-semibold sm:text-lg" style={{ color: '#111827' }}>Report generated successfully</h4>
	                    <p className="mt-1 text-xs sm:text-sm" style={{ color: '#64748b' }}>Select what you want to do next.</p>
	                  </div>
	                  <span className="inline-flex w-fit rounded-full border px-2.5 py-1 text-[11px] font-semibold" style={{ borderColor: '#bbf7d0', backgroundColor: '#f0fdf4', color: '#166534' }}>
	                    Ready
	                  </span>
	                </div>
		                <div className={`grid grid-cols-1 gap-3 ${permissions?.aichat === 1 ? 'sm:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-3'}`}>
		                  <button
		                    type="button"
		                    className="group flex min-h-[96px] items-center gap-4 rounded-md border px-4 py-4 text-left transition hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 sm:flex-col sm:items-center sm:justify-center sm:gap-3 sm:text-center xl:min-h-[132px]"
		                    style={{ borderColor: '#cbd5e1', backgroundColor: '#ffffff', color: '#166534' }}
		                    disabled={!reportData?.Table?.[0]?.RevXlUrlPath}
		                    onClick={() => window.open(reportData?.Table?.[0]?.RevXlUrlPath, '_blank')}
		                  >
		                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-green-50 transition group-hover:bg-green-100">
		                      <FileSpreadsheet size={24} />
		                    </span>
		                    <span className="min-w-0">
		                      <span className="block text-sm font-semibold">Export Excel</span>
		                      <span className="mt-0.5 block text-xs font-normal text-slate-500">Download file</span>
		                    </span>
		                  </button>
		                  <button
		                    type="button"
		                    className="group flex min-h-[96px] items-center gap-4 rounded-md border px-4 py-4 text-left transition hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 sm:flex-col sm:items-center sm:justify-center sm:gap-3 sm:text-center xl:min-h-[132px]"
		                    style={{ borderColor: '#cbd5e1', backgroundColor: '#ffffff', color: '#1d4ed8' }}
		                    disabled={!reportData?.Table?.[0]?.HTMlString}
		                    onClick={openReportPreview}
		                  >
		                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-blue-50 transition group-hover:bg-blue-100">
		                      <Eye size={24} />
		                    </span>
		                    <span className="min-w-0">
		                      <span className="block text-sm font-semibold">Print Preview</span>
		                      <span className="mt-0.5 block text-xs font-normal text-slate-500">Open preview</span>
		                    </span>
		                  </button>
		                  <button
		                    type="button"
		                    className="group flex min-h-[96px] items-center gap-4 rounded-md border px-4 py-4 text-left transition hover:-translate-y-0.5 hover:shadow-md sm:flex-col sm:items-center sm:justify-center sm:gap-3 sm:text-center xl:min-h-[132px]"
		                    style={{ borderColor: '#cbd5e1', backgroundColor: '#ffffff', color: '#4338ca' }}
			                    onClick={showReportData}
			                  >
		                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-indigo-50 transition group-hover:bg-indigo-100">
		                      <Code size={24} />
		                    </span>
		                    <span className="min-w-0">
		                      <span className="block text-sm font-semibold">Show Datas</span>
		                      <span className="mt-0.5 block text-xs font-normal text-slate-500">View tables</span>
		                    </span>
			                  </button>
			                  {permissions?.aichat === 1 && (
			                    <button
			                      type="button"
			                      className="group flex min-h-[96px] items-center gap-4 rounded-md border px-4 py-4 text-left transition hover:-translate-y-0.5 hover:shadow-md sm:flex-col sm:items-center sm:justify-center sm:gap-3 sm:text-center xl:min-h-[132px]"
			                      style={{ borderColor: '#cbd5e1', backgroundColor: '#ffffff', color: '#7e22ce' }}
			                      onClick={showReportDataWithAi}
			                    >
		                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-purple-50 transition group-hover:bg-purple-100">
		                        <Bot size={24} />
		                      </span>
		                      <span className="min-w-0">
		                        <span className="block text-sm font-semibold">Show Datas + AI</span>
		                        <span className="mt-0.5 block text-xs font-normal text-slate-500">Open assistant</span>
		                      </span>
			                    </button>
			                  )}
	                </div>
	              </div>
	            </div>
	          )}
	        </div>

        {/* AI Assistant floating card */}
        <div ref={aiChatCardRef} className={`fixed flex flex-col overflow-hidden rounded-xl border shadow-2xl ${
          aiIsDragging ? 'transition-none' : 'transition-all duration-300 ease-in-out'
        } ${
          showAiChat
            ? aiIsMinimized
              ? 'z-[70] h-14 w-[calc(100vw-1rem)] max-w-sm translate-y-0 opacity-100 sm:w-80'
              : 'z-[70] h-[min(720px,calc(100vh-2rem))] w-[calc(100vw-1rem)] max-w-lg translate-y-0 opacity-100 sm:w-[460px]'
            : 'pointer-events-none h-0 w-0 translate-y-4 opacity-0'
        }`} style={{
          backgroundColor: 'var(--base-200)',
          borderColor: 'var(--base-300)',
          ...(aiChatPosition ? { left: aiChatPosition.left, top: aiChatPosition.top } : { right: 16, bottom: 16 })
        }}>
          {showAiChat && (
            <>
              <div
                className={`${aiIsMinimized ? 'p-2' : 'p-3 sm:p-4'} border-b shadow-sm cursor-move select-none`}
                onPointerDown={handleAiChatDragStart}
                title="Drag to move AI Assistant"
                style={{ borderColor: 'var(--base-300)', backgroundColor: 'var(--base-100)' }}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="flex items-center gap-2 text-base font-semibold text-purple-700">
                      <Bot size={19} />
                      AI Assistant
                    </h3>
                    {!aiIsMinimized && <p className="mt-0.5 text-[11px] text-gray-500">Drag the header to move</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => setAiIsMinimized(!aiIsMinimized)}
                      className="flex items-center justify-center w-8 h-8 text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
                      title={aiIsMinimized ? "Maximize AI Assistant" : "Minimize AI Assistant"}
                    >
                      {aiIsMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                    </button>
                    {!aiIsMinimized && (
                      <>
                    <button
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={handleToggleAiPromptHistory}
                      className={`flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${
                        showAiPromptHistory
                          ? 'text-purple-700 bg-purple-100 border-purple-200'
                          : 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      title="Show saved prompts"
                    >
                      <History size={16} />
                    </button>
                      </>
                    )}
                    <button
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => { setShowAiChat(false); setAiIsMinimized(false); }}
                      className="flex items-center justify-center w-7 h-7 text-sm font-semibold text-gray-500 bg-white border border-gray-200 rounded-md hover:text-gray-800 hover:bg-gray-50"
                      title="Close AI Assistant"
                    >
                      <X size={15} />
                    </button>
                  </div>
                </div>
                {aiPromptSaveStatus && (
                  <div className="inline-flex px-2 py-1 mt-2 text-[11px] font-medium text-purple-700 bg-purple-50 border border-purple-100 rounded-md">
                    {aiPromptSaveStatus}
                  </div>
                )}
              </div>

              {!aiIsMinimized && (
              <div className="flex flex-col flex-1 min-h-0 gap-3 p-3 overflow-hidden sm:p-4">
                {showAiPromptHistory && (
                  <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-purple-700">Prompt History</span>
                      <button
                        onClick={loadAiPromptHistory}
                        className="px-2 py-1 text-xs font-semibold text-purple-700 bg-white border border-purple-100 rounded-md hover:text-purple-900 hover:border-purple-300"
                        disabled={aiPromptHistoryLoading}
                      >
                        {aiPromptHistoryLoading ? "Loading..." : "Refresh"}
                      </button>
                    </div>

                    {aiPromptHistoryError ? (
                      <div className="text-xs text-red-600">{aiPromptHistoryError}</div>
                    ) : aiPromptHistory.length === 0 && !aiPromptHistoryLoading ? (
                      <div className="text-xs text-gray-500">No saved prompts found</div>
                    ) : (
                      <div className="pr-1 space-y-2 overflow-y-auto max-h-44">
                        {aiPromptHistory.map((item) => (
                          <button
                            key={item.Id}
                            type="button"
                            onClick={() => setAiInputText(item.Prompt || "")}
                            className="w-full p-2.5 text-left bg-white border border-purple-100 rounded-md hover:border-purple-300 hover:bg-purple-100"
                            title="Use this prompt"
                          >
                            <div className="text-sm font-semibold text-gray-800 truncate">{item.Prompt}</div>
                            <div className="mt-1 text-[10px] text-gray-500 truncate">
                              {item.ReportTitle || "No report title"} · {item.EntryTime ? new Date(item.EntryTime).toLocaleString() : ""}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {aiAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 border border-blue-200 rounded-lg bg-blue-50">
                    {aiAttachments.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-1 text-sm bg-white border border-blue-300 rounded-md">
                        <span className="truncate max-w-32">{file.name}</span>
                        <button
                          onClick={() => setAiAttachments(prev => prev.filter((_, i) => i !== idx))}
                          className="text-lg text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
<div className="flex items-end gap-2">
                  <button
                    onClick={() => aiFileInputRef.current?.click()}
                    className="flex items-center justify-center w-10 h-10 text-gray-500 transition-all duration-200 bg-white border border-gray-200 rounded-lg shrink-0 hover:text-purple-600 hover:bg-purple-50 hover:border-purple-300"
                    title="Attach files"
                  >
                    <Plus size={18} />
                  </button>
                  <input
                    ref={aiFileInputRef}
                    type="file"
                    multiple
                    onChange={handleAiFileSelect}
                    className="hidden"
                  />
                  <div className="flex-1 min-w-0">
                   <textarea
  value={aiInputText}
  onChange={(e) => setAiInputText(e.target.value)}
  onKeyDown={handleAiKeyPress}
  placeholder="Ask anything about your report..."
  className="w-full px-3 py-2 text-sm leading-relaxed transition-all duration-200 bg-white border border-gray-300 rounded-lg shadow-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
  rows={3}
  style={{
    minHeight: "80px",
    maxHeight: "120px",
    fontSize: "14px"
  }}
/>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <div className="flex gap-2">
                      <button
                        onClick={startAiVoiceRecognition}
                        className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 border ${
                          aiIsListening
                            ? 'bg-red-500 text-white border-red-500 animate-pulse'
                            : 'text-gray-500 hover:text-purple-500 border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                        }`}
                        title="Voice input"
                      >
                        <Mic size={16} />
                      </button>
                      <button
                        onClick={handleAiSend}
                        disabled={aiIsLoading || !aiInputText.trim()}
                        className="flex items-center justify-center w-10 h-10 text-white transition-all duration-200 bg-purple-600 rounded-lg shadow-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Send"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-h-0 p-3 space-y-3 overflow-y-auto border rounded-lg shadow-inner" style={{ backgroundColor: 'var(--base-100)', borderColor: 'var(--base-300)' }}>
                  {aiMessages.length === 0 && (
                    <div className="py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 text-purple-600 bg-purple-50 rounded-full">
                        <Bot size={28} />
                      </div>
                      <p className="text-sm font-medium">How can I help you with your report data?</p>
                      <p className="mt-1 text-xs text-gray-400">Ask a question, reuse history, or save useful prompts.</p>
                    </div>
                  )}
                  {aiMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {(() => {
                        const promptKey = `${msg.timestamp || idx}-${msg.content}`;
                        const isSaved = aiSavedPromptKeysRef.current.has(promptKey);
                        return (
                      <div className={`max-w-[88%] px-3 py-2 rounded-lg text-sm shadow-sm transition-all duration-200 ${
                        msg.role === 'user'
                          ? 'bg-purple-600 text-white rounded-br-sm'
                          : 'bg-gray-50 border border-gray-200 text-gray-800 rounded-bl-sm'
                      }`}>
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="pb-1 mb-2 text-xs border-b border-gray-300 opacity-75">
                            📎 {msg.attachments.join(', ')}
                          </div>
                        )}
                        {msg.timestamp && (
                          <div className="text-[10px] opacity-60 mb-1">
                            🕒 {msg.timestamp}
                          </div>
                        )}
                        <div
                          className="text-sm leading-relaxed whitespace-pre-wrap"
                          dangerouslySetInnerHTML={{ __html: msg.content }}
                        />
                        {msg.role === "user" && (
                          <div className="flex justify-end mt-2">
                            <button
                              type="button"
                              onClick={() => handleSaveAiMessagePrompt(msg.content, promptKey)}
                              disabled={isSaved}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-purple-700 bg-white rounded-md hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-70"
                              title={isSaved ? "Prompt already saved" : "Save this prompt"}
                            >
                              <Save size={12} />
                              {isSaved ? "Saved" : "Save"}
                            </button>
                          </div>
                        )}
                      </div>
                        );
                      })()}
                    </div>
                  ))}
                  {aiIsLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 border border-gray-200 px-3 py-2 rounded-lg shadow-sm animate-pulse max-w-[85%]">
                        <div className="flex items-center space-x-2">
                          <Loader className="animate-spin" size={14} />
                          <span className="text-xs text-gray-600">AI is analyzing your data...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={aiMessagesEndRef} />
                </div>
              </div>
              )}
            </>
          )}
        </div>
      </div>

    </div>
  </div>
)}

</div>
);


};


// -----------------------------
// Page Wrapper (login-style background)
// -----------------------------
const AdvanceReport = () => {
  const navigate = useNavigate();
  
  // ESC key handler to go back to explorer
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        navigate("/explorer");
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [navigate]);
  
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--base-200)' }}
    >
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-20 flex items-center justify-between gap-3 px-4 py-3 shadow-sm" style={{ backgroundColor: 'var(--base-100)', borderBottom: '1px solid var(--base-300)' }}>
        <button onClick={() => navigate("/explorer")} className="px-4 py-2 text-sm font-medium transition-colors border rounded-md" style={{ color: 'var(--base-content)', backgroundColor: 'var(--base-200)', borderColor: 'var(--base-300)' }}>
          ← Back to Explorer
        </button>
        <h1 className="text-lg font-bold text-center md:text-xl" style={{ color: 'var(--base-content)' }}>Advance Report Generator</h1>
        <div className="w-[132px] hidden sm:block"></div>
      </div>

      <div className="pt-20 px-3 md:px-6 pb-6 min-h-[calc(100vh-5rem)]">
        <ReportGenerator />
      </div>
    </div>
  );
};

export default AdvanceReport;

 

